import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@/environments/environment';
import { CardListing, CreateListingRequest, ListingSortBy, ListingSortDir, ListingStatus, MarketplaceSeller, SaleRecord } from '@/app/core/models/marketplace.model';
import { CardRarity } from '@/app/shared/components/card/card.model';
import { Page } from '@/app/core/models/page';

export interface GetListingsParams {
	page?: number;
	size?: number;
	sortBy?: ListingSortBy;
	/** Omis = défaut auto côté back : asc pour price, desc sinon. */
	sortDir?: ListingSortDir;
	rarity?: CardRarity;
	cardId?: string;
	cardName?: string;
	minPrice?: number;
	maxPrice?: number;
	sellerUsername?: string;
	extensionId?: string;
	/** Masque les annonces dont la carte est déjà possédée (peu importe le score) — sans effet en anonyme. */
	hideOwned?: boolean;
}

export interface GetMyListingsParams {
	status?: ListingStatus;
	page?: number;
	size?: number;
}

@Injectable({ providedIn: 'root' })
export class MarketplaceService {

	private readonly BASE_URI = `${environment.backendUrl}/marketplace`;
	private readonly SERVICE_URI = `${this.BASE_URI}/listings`;
	private readonly http = inject(HttpClient);

	getListings(params: GetListingsParams = {}): Observable<Page<CardListing>> {
		let httpParams = new HttpParams()
			.set('page', params.page ?? 0)
			.set('size', params.size ?? 20)
			.set('sortBy', params.sortBy ?? 'createdAt');
		if (params.sortDir) httpParams = httpParams.set('sortDir', params.sortDir);
		if (params.rarity) httpParams = httpParams.set('rarity', params.rarity);
		if (params.cardId) httpParams = httpParams.set('cardId', params.cardId);
		if (params.cardName) httpParams = httpParams.set('cardName', params.cardName);
		if (params.minPrice != null) httpParams = httpParams.set('minPrice', params.minPrice);
		if (params.maxPrice != null) httpParams = httpParams.set('maxPrice', params.maxPrice);
		if (params.sellerUsername) httpParams = httpParams.set('sellerUsername', params.sellerUsername);
		if (params.extensionId) httpParams = httpParams.set('extensionId', params.extensionId);
		if (params.hideOwned) httpParams = httpParams.set('hideOwned', true);

		return this.http.get<Page<CardListing>>(this.SERVICE_URI, { params: httpParams });
	}

	getSellers(): Observable<MarketplaceSeller[]> {
		return this.http.get<MarketplaceSeller[]>(`${this.BASE_URI}/sellers`);
	}

	/** DESC (plus récente en premier) — l'appelant doit reverse pour un affichage chronologique. */
	getSales(cardId: string, limit = 20): Observable<SaleRecord[]> {
		const httpParams = new HttpParams().set('cardId', cardId).set('limit', limit);
		return this.http.get<SaleRecord[]>(`${this.BASE_URI}/sales`, { params: httpParams });
	}

	getMyListings(params: GetMyListingsParams = {}): Observable<Page<CardListing>> {
		const httpParams = new HttpParams()
			.set('status', params.status ?? 'ACTIVE')
			.set('page', params.page ?? 0)
			.set('size', params.size ?? 20);

		return this.http.get<Page<CardListing>>(`${this.SERVICE_URI}/mine`, { params: httpParams });
	}

	createListing(request: CreateListingRequest): Observable<CardListing> {
		return this.http.post<CardListing>(this.SERVICE_URI, request);
	}

	updateListingPrice(id: string, price: number): Observable<CardListing> {
		return this.http.patch<CardListing>(`${this.SERVICE_URI}/${id}/price`, { price });
	}

	cancelListing(id: string): Observable<void> {
		return this.http.delete<void>(`${this.SERVICE_URI}/${id}`);
	}

	buyListing(id: string): Observable<CardListing> {
		return this.http.post<CardListing>(`${this.SERVICE_URI}/${id}/buy`, {});
	}
}
